import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import ContactForm from '../../components/ContactForm';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function ServicePageEn() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'service'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Service & Repair';
  const badge = pageData?.undertitel || 'Service & Maintenance';
  const intro =
    pageData?.intro ||
    'When a machine is idle, production stops. Our experienced service technicians provide nationwide technical service, preventive maintenance, and urgent troubleshooting on presses, sheet metal machinery, and centralized lubrication systems.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Service & Repair – Industrial Machinery & Lubrication';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'PMB Service & Repair for presses, sheet metal machinery, and central lubrication systems. Rapid response and preventive maintenance across Denmark.';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Service</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-radial from-[#006368]/20 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
          </div>
        </div>
      </section>

      {/* Service Focus Cards */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-screwdriver-wrench" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Preventive Maintenance</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Structured service agreements and periodic inspections extend machinery lifespan, minimize unscheduled breakdowns, and ensure compliance with European safety standards.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Inspection of mechanical wear components
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Hydraulics, valves, and clutch control
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Calibration and pressure testing of lubrication systems
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-truck-fast" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Emergency Repair</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Rapid on-site dispatch and expert diagnostic troubleshooting when urgent breakdowns occur. Our mobile service vans are equipped with specialized tooling and common wear parts.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Prompt telephone engineering support
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Nationwide dispatch across Denmark from Borup
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Direct access to extensive spare parts inventory
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-arrows-rotate" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Refurbishment & Retrofit</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Complete mechanical and electrical overhaul of older presses in our machine factory in Borup, or modernizing safety curtains, PLC controls, and hydraulic units on-site.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Modern safety systems & digital touchscreens
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Mechanical regrinding, re-bushing & bearing replacement
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Turnkey installation of automatic central lubrication
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Authorized SKF & Lincoln Partner Banner */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl bg-stone-900 text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-[#0d8086]">
                Certified Service Partner
              </span>
              <h2 className="text-2xl font-bold">
                SKF & Lincoln Central Lubrication Service
              </h2>
              <p className="text-sm text-stone-400 max-w-xl">
                We design, install, repair, and optimize central lubrication systems for all industrial sectors, agricultural machinery, and heavy transport.
              </p>
            </div>
            <Link
              to="/en/central-lubrication"
              className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition whitespace-nowrap shrink-0"
            >
              Learn about Central Lubrication
            </Link>
          </div>
        </div>
      </section>

      {/* Book Service / Inquiry Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#006368] block mb-2">
              Book Technician or Inquire
            </span>
            <h2 className="text-3xl font-extrabold text-stone-900">
              Need technical service or repair?
            </h2>
            <p className="mt-2 text-sm text-stone-600">
              Contact our service department directly by phone or submit your service request below.
            </p>
          </div>

          <div className="mb-8 p-4 rounded-lg bg-[#006368]/10 border border-[#006368]/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#006368] text-white flex items-center justify-center shrink-0">
                <i className="fa-solid fa-phone" />
              </span>
              <div>
                <div className="text-xs text-stone-500 font-medium">Urgent service phone line:</div>
                <a
                  href="tel:+4557526262"
                  className="text-lg font-extrabold text-[#006368] hover:underline"
                >
                  +45 57 52 62 62
                </a>
              </div>
            </div>
            <span className="text-xs text-stone-500">Fast response nationwide</span>
          </div>

          <ContactForm defaultSubject="Inquiry regarding machine service" lang="en" />
        </div>
      </section>
    </>
  );
}
